(function () {
  // ADR-176 — web client for the same assistant-worker SSE endpoint the
  // Flutter app calls (cloudflare/assistant-worker, POST /api/v1/chat).
  // No auth required, CORS open, stateless — client resends full history
  // each turn. See mobile_app/lib/features/assistant/data/datasources/
  // assistant_sse_datasource.dart for the reference contract this mirrors.
  //
  // ADR-195 (2026-10-01): the old 2,527-line file was split by feature into
  // four ordered files sharing the window.ErosVenus namespace — eros-lazy.js
  // loads them in this order (script.async=false keeps execution order):
  //   eros-assistant-storage.js  localStorage index/messages, owner scoping,
  //                              legacy migration, IndexedDB thumbnails
  //   eros-assistant-api.js      plain fetches to the worker (list, one
  //                              transcript, delete, rename, images, claim,
  //                              feedback) + BASE_URL/CHAT_PATH
  //   eros-assistant-panel.js    the ☰ conversation side panel + its pure
  //                              helpers (pickEntry/groupByDate/search)
  //   eros-assistant.js (this)   chat: stream, rendering, composer, voice,
  //                              images, cart CTA, identity — and wiring,
  //                              including the smart-resume entry rule.
  // The moved code is unchanged; the aliases below keep its call sites as
  // they were.
  var V = window.ErosVenus;
  if (!V || !V.storage || !V.api || !V.panel || !V.conversations) {
    // Load-order guard: never run half-wired (e.g. a stale cached layout
    // that only lists this file).
    if (window.console) window.console.warn('[eros-assistant] ErosVenus modules missing — not starting');
    return;
  }
  var S = V.storage;
  var A = V.api;

  var BASE_URL = A.BASE_URL;
  var CHAT_PATH = A.CHAT_PATH;
  var ANON_ID_KEY = S.ANON_ID_KEY;
  var CLAIM_FLAG_KEY = S.CLAIM_FLAG_KEY;
  var TITLE_MAX_CHARS = S.TITLE_MAX_CHARS;
  var ownerSuffix = S.ownerSuffix;
  var uid = S.uid;
  var listConversations = S.listConversations;
  var notifyConversationsChanged = S.notifyConversationsChanged;
  var saveConversationMeta = S.saveConversationMeta;
  var renameConversation = S.renameConversation;
  var loadMessages = S.loadMessages;
  var saveMessages = S.saveMessages;
  var anonId = S.anonId;
  var saveAnonId = S.saveAnonId;
  var saveTurnImages = S.saveTurnImages;
  var loadTurnImages = S.loadTurnImages;
  var customerId = A.customerId;
  var fetchServerImageDataUrl = A.fetchServerImageDataUrl;
  var maybeClaimGuestConversations = A.maybeClaimGuestConversations;

  var panel = document.getElementById('eros-assistant-panel');
  if (!panel) return;

  var messagesEl = document.getElementById('eros-assistant-messages');
  var chipsEl = document.getElementById('eros-assistant-chips');
  var form = document.getElementById('eros-assistant-form');
  var input = form ? form.querySelector('.eros-assistant-panel__input') : null;
  var sendButton = form ? form.querySelector('.eros-assistant-panel__send') : null;
  // ADR-195: ☰ at the start of the header opens the conversation side panel
  // (assets/eros-assistant-panel.js) that replaced the history button + modal.
  var menuButton = document.getElementById('eros-assistant-menu-button');
  var newChatButton = document.getElementById('eros-assistant-new-chat-button');
  var deletePhotosButton = document.getElementById('eros-assistant-delete-photos-button');
  var sidebarEl = document.getElementById('eros-assistant-sidebar');
  var identityButton = document.getElementById('eros-assistant-identity-button');
  var identityOverlay = document.getElementById('eros-assistant-identity');
  var identityCloseButton = document.getElementById('eros-assistant-identity-close');
  var currencyCode = panel.dataset.currency || 'USD';

  // Missing-locale guard: Shopify renders "Translation missing: …" for keys
  // absent from a locale file (only ar.json carries the newest keys) — treat
  // those as absent so the Arabic JS fallbacks apply instead of leaking the
  // marker into aria-labels and hints.
  (function sanitizeStrings() {
    var s = window.ErosAssistantStrings;
    if (!s) return;
    Object.keys(s).forEach(function (k) {
      if (typeof s[k] === 'string' && s[k].indexOf('Translation missing') === 0) delete s[k];
    });
  })();

  var history = [];
  var streaming = false;
  var hasMessages = false;
  var lastFailedMessage = null;
  var currentConversationId = null;

  // ---- Cross-device image sync (owner order 2026-08-25): download the
  // stored photos a server transcript references (api.fetchServerImageDataUrl)
  // into the IndexedDB store (storage.saveTurnImages) so a thread started on
  // the Flutter app (or another browser) shows its photos here too.
  function downloadServerImages(conversationId, msgs) {
    var pending = (msgs || []).filter(function (m) {
      return m && m.imgKey && m.srvImages && m.srvImages.length;
    });
    if (!pending.length || !(window.ErosSession && window.ErosSession.isSignedIn())) return;
    var work = pending.map(function (m) {
      return loadTurnImages(m.imgKey).then(function (existing) {
        if (existing && existing.length) return false; // already cached
        return Promise.all(
          m.srvImages.map(function (imageId) {
            return fetchServerImageDataUrl(imageId);
          }),
        ).then(function (urls) {
          var ok = urls.filter(Boolean);
          if (!ok.length) return false;
          return saveTurnImages(m.imgKey, conversationId, ok).then(function () {
            return true;
          });
        });
      });
    });
    Promise.all(work).then(function (results) {
      // Re-render once so the markers swap for the freshly-cached thumbs —
      // only if this thread is still the open one.
      if (results.indexOf(true) === -1) return;
      if (currentConversationId !== conversationId) return;
      renderMessagesFromStorage(loadMessages(conversationId));
    });
  }

  // Markdown-lite renderer (owner UI review 2026-08-18): the model emits
  // **bold** and "* " bullets; the panel used to strip ** crudely and leave
  // bullet asterisks raw. Builds DOM nodes (never innerHTML from model text)
  // so nothing can inject; unpaired ** — e.g. mid-stream — renders plain.
  function renderAssistantText(el, text) {
    el.textContent = '';
    var lines = (text || '').split('\n');
    for (var li = 0; li < lines.length; li++) {
      if (li > 0) el.appendChild(document.createElement('br'));
      var line = lines[li];
      var bulletMatch = line.match(/^\s*[*\-•]\s+/);
      if (bulletMatch) {
        line = line.slice(bulletMatch[0].length);
        var dot = document.createElement('b');
        dot.textContent = '•  ';
        el.appendChild(dot);
      }
      line = line.replace(/^\s*#{1,4}\s+/, '').replace(/`/g, '');
      var re = /\*\*(.+?)\*\*/g;
      var cursor = 0;
      var m;
      while ((m = re.exec(line)) !== null) {
        if (m.index > cursor) {
          el.appendChild(
            document.createTextNode(line.slice(cursor, m.index).replace(/\*/g, '')),
          );
        }
        var b = document.createElement('b');
        b.textContent = m[1];
        el.appendChild(b);
        cursor = m.index + m[0].length;
      }
      if (cursor < line.length) {
        el.appendChild(document.createTextNode(line.slice(cursor).replace(/\*/g, '')));
      }
    }
  }

  function autoTitle(text) {
    var trimmed = (text || '').trim();
    return trimmed.length > TITLE_MAX_CHARS ? trimmed.slice(0, TITLE_MAX_CHARS) + '…' : trimmed;
  }

  function languageCode() {
    var iso = (document.documentElement.getAttribute('lang') || 'ar').split('-')[0];
    return iso.toUpperCase();
  }

  if (window.ErosSession && typeof window.ErosSession.onChange === 'function') {
    window.ErosSession.onChange(maybeClaimGuestConversations);

    // Identity-change reset (audit 2026-08-17): without this, A logs out and
    // B logs in on the same page and B sees A's open transcript, and B's next
    // message would re-send A's in-memory history under B's token. Track the
    // storage suffix; when it changes, hard-reset the panel state and
    // re-render the (now per-account) conversation list.
    var lastOwnerSuffix = ownerSuffix();
    window.ErosSession.onChange(function (session) {
      var next = ownerSuffix();
      if (next === lastOwnerSuffix) return;
      lastOwnerSuffix = next;
      if (streaming) stopStreaming();
      stopVoice(true);
      clearPendingImages();
      // ADR-195: the open side panel lists the previous identity's threads.
      if (sidebar) sidebar.close(false);
      history = [];
      currentConversationId = null;
      hasMessages = false;
      lastFailedMessage = null;
      lastFailedImages = null;
      pinnedToBottom = true;
      updateJumpButton();
      if (messagesEl) messagesEl.innerHTML = '';
      renderSuggestionChips();
      updateNewChatVisibility();
      notifyConversationsChanged();
      // Logout → rotate the guest identity: the old anon id was (or will be)
      // claimed by the account that just left; reusing it would let the NEXT
      // account adopt the new guest's chats. A fresh guest gets a fresh
      // server-minted id on their first reply.
      if (!session || !session.customerGid) {
        try {
          window.localStorage.removeItem(ANON_ID_KEY);
          window.localStorage.removeItem(CLAIM_FLAG_KEY);
        } catch (e) {
          /* storage unavailable */
        }
      }
    });
  }

  function formatPrice(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currencyCode;
    }
  }

  // Static chrome icons only — never model text (innerHTML is safe here for
  // the same reason STOP_ICON_SVG below is).
  var JUMP_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M12 5v14"></path><path d="M5 12l7 7 7-7"></path></svg>';
  var COPY_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<rect x="9" y="9" width="11" height="11" rx="2"></rect><path d="M5 15V6a2 2 0 0 1 2-2h9"></path></svg>';
  var CHECK_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M4 12.5l5 5L20 6.5"></path></svg>';
  var REGEN_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<polyline points="23 4 23 10 17 10"></polyline>' +
    '<path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>';
  var MIC_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<rect x="9" y="2.5" width="6" height="11" rx="3"></rect>' +
    '<path d="M5 11a7 7 0 0 0 14 0"></path><path d="M12 18v3.5"></path></svg>';
  var ATTACH_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M19.5 11.5l-7.6 7.6a4.6 4.6 0 0 1-6.5-6.5l7.8-7.8a3.1 3.1 0 0 1 4.3 4.4l-7.7 7.7a1.5 1.5 0 0 1-2.2-2.2l7-7"></path>' +
    '</svg>';

  // Pinned-follow scrolling (audit vs Gemini/ChatGPT web, 2026-08-18):
  // scrollToEnd used to fire on EVERY delta, yanking a user who scrolled up
  // to re-read back to the bottom. Auto-scroll now only runs while the view
  // is "pinned" (at/near the bottom); scrolling up detaches, and a floating
  // jump pill re-attaches.
  var PIN_THRESHOLD_PX = 80;
  var pinnedToBottom = true;
  var jumpAutoScrollUntil = 0;
  var jumpButton = null;

  function isNearBottom() {
    if (!messagesEl) return true;
    return messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight <= PIN_THRESHOLD_PX;
  }

  function updateJumpButton() {
    if (jumpButton) jumpButton.hidden = pinnedToBottom;
  }

  function scrollToEnd(force) {
    // wpm_10 P0: scroll the messages scrollport (not drawer::part(body)).
    if (!messagesEl) return;
    if (!force && !pinnedToBottom) return;
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  if (messagesEl) {
    jumpButton = document.createElement('button');
    jumpButton.type = 'button';
    jumpButton.className = 'eros-assistant-jump';
    jumpButton.hidden = true;
    var jumpLabel =
      (window.ErosAssistantStrings && window.ErosAssistantStrings.jumpLatest) || 'الأحدث';
    jumpButton.setAttribute('aria-label', jumpLabel);
    jumpButton.title = jumpLabel;
    jumpButton.innerHTML = JUMP_ICON_SVG;
    jumpButton.addEventListener('click', function () {
      pinnedToBottom = true;
      updateJumpButton();
      // Ignore the not-yet-at-bottom scroll events the smooth scroll itself
      // emits — they must not re-detach mid-flight.
      jumpAutoScrollUntil = Date.now() + 700;
      try {
        messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
      } catch (e) {
        messagesEl.scrollTop = messagesEl.scrollHeight;
      }
    });
    // Sibling of the scrollport (absolute within the drawer body) so it
    // floats over the list instead of scrolling away with it.
    messagesEl.insertAdjacentElement('afterend', jumpButton);

    messagesEl.addEventListener(
      'scroll',
      function () {
        if (Date.now() < jumpAutoScrollUntil && !isNearBottom()) return;
        pinnedToBottom = isNearBottom();
        updateJumpButton();
      },
      { passive: true },
    );

    // Keyboard occlusion (2026-08-19): assets/eros-keyboard-inset.js shrinks
    // .drawer::part(base) by --eros-kb-inset so the composer rides above the
    // on-screen keyboard. That relayout shortens THIS scrollport, which would
    // otherwise slide the newest message under the composer and pop the jump
    // pill open by itself. The event is dispatched from inside the utility's
    // rAF, before the style write has been laid out, so pinnedToBottom is
    // still the pre-resize truth when read here.
    window.addEventListener('eros:keyboard-inset', function () {
      if (!messagesEl) return;
      var wasPinned = pinnedToBottom;
      // Two frames: CSS var applied, then the drawer relayout settled.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!messagesEl) return;
          if (!wasPinned) {
            updateJumpButton();
            return;
          }
          pinnedToBottom = true;
          // The shrink emits a scroll event of its own — same guard the jump
          // pill uses, so that event cannot re-detach us mid-correction.
          jumpAutoScrollUntil = Date.now() + 200;
          messagesEl.scrollTop = messagesEl.scrollHeight;
          updateJumpButton();
        });
      });
    });
  }

  function clearEmptyGreeting() {
    var empty = messagesEl.querySelector('[data-eros-assistant-empty]');
    if (empty) empty.remove();
  }

  function renderGuestHint() {
    if (!messagesEl) return;
    var existing = messagesEl.querySelector('[data-eros-assistant-guest-hint]');
    if (existing) existing.remove();
    if (customerId()) return;
    var strings = window.ErosAssistantStrings || {};
    if (!strings.guestHint) return;
    var hint = document.createElement('p');
    hint.className = 'eros-assistant-guest-hint';
    hint.setAttribute('data-eros-assistant-guest-hint', '');
    hint.textContent = strings.guestHint;
    messagesEl.insertBefore(hint, messagesEl.firstChild);
  }

  function renderEmptyGreeting() {
    if (!messagesEl) return;
    messagesEl.innerHTML = '';
    var empty = document.createElement('div');
    empty.className = 'eros-assistant-empty';
    empty.setAttribute('data-eros-assistant-empty', '');
    var welcome = document.createElement('p');
    welcome.className = 'eros-assistant-empty__welcome';
    welcome.textContent = (window.ErosAssistantStrings && window.ErosAssistantStrings.welcome) || '';
    empty.appendChild(welcome);
    messagesEl.appendChild(empty);
    renderGuestHint();
  }

  function appendBubble(role) {
    clearEmptyGreeting();
    var bubble = document.createElement('div');
    bubble.className = 'eros-assistant-bubble eros-assistant-bubble--' + role;
    var text = document.createElement('p');
    bubble.appendChild(text);
    messagesEl.appendChild(bubble);
    scrollToEnd();
    return { bubble: bubble, text: text };
  }

  // Sent-image preview: the downscaled data URI is rendered above the text
  // of the user bubble that carried it. Nothing here reaches localStorage —
  // persistExchange stores the data URIs in the IndexedDB image store and
  // keeps only an `imgKey` pointer in the entry. Called once per attached
  // image: inserting before lastChild (the bubble's <p>, always the last
  // node while thumbs are being added) keeps multiple thumbs in pick order —
  // firstChild-insertion would reverse them.
  function attachUserThumb(bubbleEl, src) {
    if (!bubbleEl || !src) return;
    var strings = window.ErosAssistantStrings || {};
    var img = document.createElement('img');
    img.className = 'eros-assistant-bubble__image';
    img.src = src;
    img.alt = strings.imageAttached || 'صورة مرفقة';
    bubbleEl.insertBefore(img, bubbleEl.lastChild);
    // The thumbnail decodes after layout — keep a pinned view at the bottom.
    img.addEventListener('load', function () { scrollToEnd(); }, { once: true });
  }

  // Restored-from-storage fallback: shown when the turn's thumbnails are not
  // (or not yet) available from the IndexedDB image store — a server-hydrated
  // thread, or a browser whose IndexedDB write failed. Returns the marker
  // element so restoreUserImages can swap it for the real thumbs.
  function attachUserImageMarker(bubbleEl) {
    if (!bubbleEl) return null;
    var strings = window.ErosAssistantStrings || {};
    var mark = document.createElement('span');
    mark.className = 'eros-assistant-bubble__image-mark';
    mark.textContent = '📎 ' + (strings.imageAttached || 'صورة مرفقة');
    bubbleEl.insertBefore(mark, bubbleEl.firstChild);
    return mark;
  }

  // ADR-273: the turn's photo was deleted by the customer — «صورة محذوفة»
  // stands where the thumbnail (or the 📎 mark) was; the message stays.
  function attachDeletedImageMark(bubbleEl) {
    if (!bubbleEl) return;
    var strings = window.ErosAssistantStrings || {};
    var mark = document.createElement('span');
    mark.className = 'eros-assistant-bubble__image-mark eros-assistant-bubble__image-mark--deleted';
    mark.textContent = strings.photoDeleted || 'صورة محذوفة';
    bubbleEl.insertBefore(mark, bubbleEl.firstChild);
  }

  // After a successful delete: every photo (thumb or mark) on screen becomes
  // the deleted mark, once per bubble.
  function markOnScreenPhotosDeleted() {
    if (!messagesEl) return;
    messagesEl.querySelectorAll('.eros-assistant-bubble--user').forEach(function (bubble) {
      var shown = bubble.querySelectorAll('.eros-assistant-bubble__image, .eros-assistant-bubble__image-mark');
      if (!shown.length) return;
      shown.forEach(function (el) { el.remove(); });
      attachDeletedImageMark(bubble);
    });
  }

  // ADR-273 «احذف صوري»: an in-panel confirmation, then the server delete;
  // only a 200 clears the device's copies.
  function openDeletePhotosConfirm() {
    if (!panel || panel.querySelector('.eros-assistant-confirm')) return;
    var strings = window.ErosAssistantStrings || {};
    var box = document.createElement('div');
    box.className = 'eros-assistant-confirm';
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-modal', 'true');
    var card = document.createElement('div');
    card.className = 'eros-assistant-confirm__card';
    var title = document.createElement('p');
    title.className = 'eros-assistant-confirm__title';
    title.textContent = strings.deletePhotosTitle || '';
    var body = document.createElement('p');
    body.className = 'eros-assistant-confirm__body';
    body.textContent = strings.deletePhotosBody || '';
    var confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'eros-assistant-confirm__button eros-assistant-confirm__button--danger';
    confirm.textContent = strings.deletePhotosConfirm || '';
    var keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'eros-assistant-confirm__button';
    keep.textContent = strings.deletePhotosCancel || '';
    function close() { box.remove(); if (deletePhotosButton) deletePhotosButton.focus(); }
    keep.addEventListener('click', close);
    box.addEventListener('keydown', function (e) { if (e.key === 'Escape') { e.stopPropagation(); close(); } });
    confirm.addEventListener('click', function () {
      confirm.disabled = true;
      keep.disabled = true;
      A.deleteMyPhotos().then(function (outcome) {
        if (outcome === 'deleted') {
          S.deleteAllImages();
          S.markImagesDeleted();
          markOnScreenPhotosDeleted();
        }
        body.textContent = outcome === 'deleted'
          ? strings.photosDeleted || ''
          : outcome === 'rate_limited' ? strings.photosDeleteRateLimited || '' : strings.photosDeleteFailed || '';
        title.textContent = '';
        confirm.remove();
        keep.disabled = false;
        keep.textContent = strings.photosDone || '';
        keep.focus();
      });
    });
    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(confirm);
    card.appendChild(keep);
    box.appendChild(card);
    panel.appendChild(box);
    keep.focus();
  }

  // Restore path (owner order 2026-08-25): marker first (sync render stays
  // instant), then the IndexedDB read replaces it with the actual thumbnails
  // in pick order. On any miss the marker simply stands — old behavior.
  function restoreUserImages(bubbleEl, imgKey) {
    var mark = attachUserImageMarker(bubbleEl);
    loadTurnImages(imgKey).then(function (imgs) {
      if (!imgs || !imgs.length || !bubbleEl.isConnected) return;
      if (mark && mark.parentNode) mark.parentNode.removeChild(mark);
      imgs.forEach(function (src) { attachUserThumb(bubbleEl, src); });
    });
  }

  function removeErrorRows() {
    if (!messagesEl) return;
    messagesEl.querySelectorAll('[data-eros-assistant-error]').forEach(function (el) {
      el.remove();
    });
  }

  function appendError(message, retryLabel) {
    removeErrorRows();
    clearEmptyGreeting();
    var row = document.createElement('div');
    row.className = 'eros-assistant-error';
    row.setAttribute('data-eros-assistant-error', '');
    var text = document.createElement('p');
    text.className = 'eros-assistant-error__message';
    text.textContent = message;
    var retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'eros-assistant-error__retry';
    retryButton.textContent = retryLabel || 'Retry';
    retryButton.addEventListener('click', function () {
      // An image-only turn has an empty message — retry must still be live.
      if (streaming || (!lastFailedMessage && !lastFailedImages)) return;
      removeErrorRows();
      submitMessage(lastFailedMessage, lastFailedImages);
    });
    row.appendChild(text);
    row.appendChild(retryButton);
    messagesEl.appendChild(row);
    scrollToEnd();
  }

  function updateNewChatVisibility() {
    if (newChatButton) newChatButton.hidden = !hasMessages;
  }

  function appendStatus(label) {
    clearEmptyGreeting();
    var status = document.createElement('div');
    status.className = 'eros-assistant-status';
    var text = document.createElement('span');
    text.textContent = label;
    var dots = document.createElement('span');
    dots.className = 'eros-assistant-status__dots';
    for (var i = 0; i < 3; i++) {
      var dot = document.createElement('span');
      dot.className = 'eros-assistant-status__dot';
      dots.appendChild(dot);
    }
    status.appendChild(text);
    status.appendChild(dots);
    messagesEl.appendChild(status);
    scrollToEnd();
    return { el: status, textEl: text };
  }

  // ---- Bubble actions: copy / thumbs feedback / regenerate (audit vs
  // Gemini/ChatGPT web, 2026-08-18). The row is DOM-built chrome appended
  // inside the assistant bubble, under the text.

  // The rendered <p> uses <br> for newlines, so a bare textContent would
  // glue lines together — rebuild plain text with the breaks preserved
  // (and without the streaming cursor glyph).
  function bubblePlainText(textEl) {
    if (!textEl) return '';
    var out = '';
    var nodes = textEl.childNodes;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeName === 'BR') out += '\n';
      else if (n.classList && n.classList.contains('eros-assistant-cursor')) continue;
      else out += n.textContent;
    }
    return out;
  }

  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch (e) {
      return false;
    }
  }

  function isLastAssistantBubble(bubbleEl) {
    if (!messagesEl || !bubbleEl) return false;
    var bubbles = messagesEl.querySelectorAll('.eros-assistant-bubble--assistant');
    return bubbles.length > 0 && bubbles[bubbles.length - 1] === bubbleEl;
  }

  // One-shot verdict persisted on the assistant entry in the per-account
  // local message store (fb: 'up'|'down') so it survives reloads.
  function persistFeedback(msgIndex, verdict) {
    if (!currentConversationId) return;
    var stored = loadMessages(currentConversationId);
    var idx = msgIndex;
    if (idx == null) {
      for (var i = stored.length - 1; i >= 0; i--) {
        if (stored[i] && stored[i].role === 'assistant') {
          idx = i;
          break;
        }
      }
    }
    if (idx == null || !stored[idx] || stored[idx].role !== 'assistant') return;
    stored[idx].fb = verdict;
    saveMessages(currentConversationId, stored);
  }

  // Fire-and-forget feedback POST — the request lives in api.postFeedback.
  function postFeedback(verdict, seq) {
    A.postFeedback(currentConversationId, verdict, seq);
  }

  // opts: { seq: number|null, fb: 'up'|'down'|null, msgIndex: number|null }
  function attachAssistantActions(bubbleEl, opts) {
    if (!bubbleEl || bubbleEl.querySelector('.eros-assistant-actions')) return;
    opts = opts || {};
    var strings = window.ErosAssistantStrings || {};
    var textEl = bubbleEl.querySelector('p');
    var row = document.createElement('div');
    row.className = 'eros-assistant-actions';

    var copyLabel = strings.copy || 'نسخ';
    var copyButton = document.createElement('button');
    copyButton.type = 'button';
    copyButton.className = 'eros-assistant-action eros-assistant-action--copy';
    copyButton.setAttribute('aria-label', copyLabel);
    copyButton.title = copyLabel;
    copyButton.innerHTML = COPY_ICON_SVG;
    copyButton.addEventListener('click', function () {
      var text = bubblePlainText(textEl);
      if (!text) return;
      var markCopied = function () {
        copyButton.innerHTML = CHECK_ICON_SVG;
        copyButton.setAttribute('aria-label', strings.copied || 'انتسخ النص');
        window.setTimeout(function () {
          copyButton.innerHTML = COPY_ICON_SVG;
          copyButton.setAttribute('aria-label', copyLabel);
        }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(markCopied).catch(function () {
          if (legacyCopy(text)) markCopied();
        });
      } else if (legacyCopy(text)) {
        markCopied();
      }
    });
    row.appendChild(copyButton);

    var voted = opts.fb === 'up' || opts.fb === 'down' ? opts.fb : null;
    var thumbs = {};
    function applyVoted(verdict) {
      thumbs.up.disabled = true;
      thumbs.down.disabled = true;
      var chosen = verdict === 'up' ? thumbs.up : thumbs.down;
      chosen.classList.add('eros-assistant-action--active');
    }
    ['up', 'down'].forEach(function (verdict) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'eros-assistant-action eros-assistant-action--thumb';
      var label =
        verdict === 'up'
          ? strings.feedbackUp || 'إجابة مفيدة'
          : strings.feedbackDown || 'إجابة غير مفيدة';
      button.setAttribute('aria-label', label);
      button.title = label;
      button.textContent = verdict === 'up' ? '👍' : '👎';
      button.addEventListener('click', function () {
        if (voted) return;
        voted = verdict;
        applyVoted(verdict);
        persistFeedback(opts.msgIndex != null ? opts.msgIndex : null, verdict);
        // Without a seq, 'last' can only address the conversation's final
        // assistant turn — older seq-less bubbles keep the vote local-only.
        if (opts.seq != null || isLastAssistantBubble(bubbleEl)) {
          postFeedback(verdict, opts.seq != null ? opts.seq : null);
        }
      });
      thumbs[verdict] = button;
      row.appendChild(button);
    });
    if (voted) applyVoted(voted);

    bubbleEl.appendChild(row);
  }

  // Regenerate lives ONLY on the last assistant bubble — rebuild it there
  // after every append so older bubbles shed theirs.
  function refreshRegenerateAction() {
    if (!messagesEl) return;
    messagesEl.querySelectorAll('.eros-assistant-action--regen').forEach(function (b) {
      b.remove();
    });
    var bubbles = messagesEl.querySelectorAll('.eros-assistant-bubble--assistant');
    if (!bubbles.length) return;
    var row = bubbles[bubbles.length - 1].querySelector('.eros-assistant-actions');
    if (!row) return;
    var strings = window.ErosAssistantStrings || {};
    var label = strings.regenerate || 'إعادة الإجابة';
    var regenButton = document.createElement('button');
    regenButton.type = 'button';
    regenButton.className = 'eros-assistant-action eros-assistant-action--regen';
    regenButton.setAttribute('aria-label', label);
    regenButton.title = label;
    regenButton.innerHTML = REGEN_ICON_SVG;
    regenButton.addEventListener('click', regenerateLast);
    row.appendChild(regenButton);
  }

  // Drop the trailing user+assistant pair from the local store so the
  // regenerated exchange replaces — not duplicates — it on the next reload.
  function removeLastExchangeFromStore() {
    if (!currentConversationId) return;
    var stored = loadMessages(currentConversationId);
    if (!stored.length) return;
    if (stored[stored.length - 1].role === 'assistant') stored.pop();
    if (stored.length && stored[stored.length - 1].role === 'user') stored.pop();
    saveMessages(currentConversationId, stored);
  }

  function regenerateLast() {
    if (streaming || !messagesEl) return;
    var bubbles = messagesEl.querySelectorAll('.eros-assistant-bubble--assistant');
    if (!bubbles.length) return;
    var last = bubbles[bubbles.length - 1];
    var userBubble = null;
    var node = last.previousElementSibling;
    while (node) {
      if (node.classList.contains('eros-assistant-bubble--user')) {
        userBubble = node;
        break;
      }
      node = node.previousElementSibling;
    }
    var message = '';
    for (var i = history.length - 1; i >= 0; i--) {
      if (history[i].role === 'user') {
        message = history[i].text || '';
        break;
      }
    }
    // Read the bubble's <p>, not the bubble: a user turn that carried an
    // image also holds a 📎 marker span whose text is not part of the prompt.
    if (!message && userBubble) {
      var userTextEl = userBubble.querySelector('p');
      message = (userTextEl ? userTextEl.textContent : userBubble.textContent) || '';
    }
    message = message.trim();
    if (!message) return;
    // Rewind in-memory history past the exchange being replaced —
    // submitMessage re-appends the user turn through the normal send path.
    if (history.length && history[history.length - 1].role === 'model') history.pop();
    if (history.length && history[history.length - 1].role === 'user') history.pop();
    removeLastExchangeFromStore();
    // Remove the exchange's DOM: from its user bubble (falling back to the
    // assistant bubble) through everything after it (cards rail, error rows).
    var cur = userBubble || last;
    while (cur) {
      var next = cur.nextElementSibling;
      cur.remove();
      cur = next;
    }
    submitMessage(message);
  }

  // ---- Cart CTA (2026-08-19, rebuilt 2026-09-25). The worker may emit a
  // `cart` SSE event right before `cards`; its `data` IS the action object
  // itself (no wrapper key):
  // { checkout_url, cart_id?, lines: [{ variant_id, quantity, handle?, title? }] }.
  //
  // The store left Shopify's hosted checkout: orders go through the theme's
  // own checkout sheet (sign-in first, cash on delivery). The worker's
  // checkout_url is a Storefront-API cart permalink, and following it
  // (owner report 2026-09-25, signed-out web chat) dropped the customer into
  // /checkouts/cn/… — the abandoned accounts flow, on a basket this browser
  // does not even own. So, exactly as the app replays `lines` into its
  // native draft cart and walks to the cart tab, this button replays them
  // into THIS browser's cart through the AJAX API and then opens the theme
  // checkout. checkout_url is ignored on purpose and never rendered.

  function variantNumericId(raw) {
    // "gid://shopify/ProductVariant/123" or "123" → "123"
    var match = /(\d+)\s*$/.exec(String(raw || '').trim());
    return match ? match[1] : null;
  }

  function cartLinesFromAction(action) {
    var out = [];
    var lines = action && Array.isArray(action.lines) ? action.lines : [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i] || {};
      var id = variantNumericId(line.variant_id);
      if (!id) continue;
      var qty = parseInt(line.quantity, 10);
      out.push({ id: id, quantity: qty > 0 ? qty : 1, title: String(line.title || line.handle || '') });
    }
    return out;
  }

  function cartApiRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var err = new Error((json && json.description) || 'cart request failed');
          err.status = res.status;
          err.responseJson = json;
          throw err;
        }
        return json;
      });
    });
  }

  function fetchCartJson() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function cartQuantityOf(cart, variantId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === String(variantId)) return items[i].quantity || 0;
    }
    return 0;
  }

  // Same bundled-sections handshake the listing quick-add uses, so the
  // drawer/header re-render from the add response instead of a second fetch.
  function collectSectionsToBundle() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  // Set semantics, like the app's upsertOne: afterwards the cart holds at
  // least the requested quantity of each line; a line already there is not
  // added again (a second tap, a restored tab). One request per line, in
  // order, so a sold-out line never vetoes the others — the app reports
  // per-line outcomes the same way.
  function replayLinesIntoCart(lines) {
    var sections = collectSectionsToBundle();
    var failures = [];
    var lastSections = null;
    return fetchCartJson()
      .then(function (cart) {
        var chain = Promise.resolve();
        lines.forEach(function (line) {
          var delta = line.quantity - cartQuantityOf(cart, line.id);
          if (delta <= 0) return;
          chain = chain.then(function () {
            return cartApiRequest('/cart/add.js', { id: line.id, quantity: delta, sections: sections.join(',') })
              .then(function (res) {
                if (res && res.sections) lastSections = res.sections;
              })
              .catch(function (err) {
                failures.push({ line: line, error: err });
              });
          });
        });
        return chain;
      })
      .then(fetchCartJson)
      .then(function (cart) {
        if (lastSections) cart.sections = lastSections;
        document.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'eros:assistant-cart', cart: cart } })
        );
        var landed = lines.filter(function (line) {
          return cartQuantityOf(cart, line.id) > 0;
        });
        return { cart: cart, failures: failures, landed: landed };
      });
  }

  function toast(message, kind) {
    if (message && window.ErosUI && typeof window.ErosUI.toast === 'function') window.ErosUI.toast(message, kind);
  }

  function openStoreCheckout() {
    // Leave the assistant the way the app leaves for its cart tab. The
    // checkout sheet is a top-layer <dialog>, so this is about focus and
    // what the customer comes back to, not stacking.
    if (panel && typeof panel.hide === 'function') {
      try {
        panel.hide();
      } catch (e) {
        /* drawer already closed */
      }
    }
    if (window.ErosCheckout && typeof window.ErosCheckout.open === 'function') {
      window.ErosCheckout.open();
      return;
    }
    // eros-checkout.js has not executed yet (it loads on idle / first cart
    // touch): a click on any [data-eros-checkout-trigger] is caught by
    // eros-lazy.js, which loads the stack and opens the sheet.
    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.hidden = true;
    trigger.setAttribute('data-eros-checkout-trigger', '');
    document.body.appendChild(trigger);
    trigger.click();
    setTimeout(function () {
      trigger.remove();
    }, 0);
  }

  // Rendered INSIDE the assistant bubble, under the reply text and above the
  // product rail (a sibling appended to the list right after this). Returns
  // whether a button was actually rendered — only lines with a parseable
  // variant id qualify; an action without them renders nothing.
  function attachCartAction(bubbleEl, action) {
    if (!bubbleEl || !action) return false;
    var lines = cartLinesFromAction(action);
    if (!lines.length) return false;
    // A repeated `cart` event replaces the row instead of stacking buttons.
    var existing = bubbleEl.querySelector('.eros-assistant-cart');
    if (existing) existing.remove();
    var strings = window.ErosAssistantStrings || {};
    var idleLabel = strings.checkout || 'إتمام الطلب';
    var row = document.createElement('div');
    row.className = 'eros-assistant-cart';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'button eros-assistant-cart__checkout';
    button.textContent = idleLabel;
    // idle → busy (lines being written) → done (they are in the cart: a later
    // tap only reopens the checkout, never adds again).
    var state = 'idle';
    button.addEventListener('click', function () {
      if (state === 'busy') return;
      if (state === 'done') {
        openStoreCheckout();
        return;
      }
      state = 'busy';
      button.disabled = true;
      button.textContent = strings.addingToCart || 'أضيفها للسلة…';
      replayLinesIntoCart(lines)
        .then(function (result) {
          state = 'done';
          button.disabled = false;
          button.textContent = idleLabel;
          var soldOut = [];
          var otherFailures = 0;
          result.failures.forEach(function (f) {
            // Shopify answers 422 for a sold-out / over-limit line, with its
            // own description; anything else is a transport failure.
            if (f.error && f.error.status === 422) soldOut.push(f.line.title || (f.error.responseJson && f.error.responseJson.description) || '');
            else otherFailures++;
          });
          if (soldOut.length) toast((strings.soldOut || 'خلصان') + ': ' + soldOut.join(' • '), 'error');
          else if (otherFailures) toast(strings.cartAddFailed || 'ما قدرت أضيفها للسلة، جرب مرة ثانية', 'error');
          if (result.landed.length) openStoreCheckout();
          else state = 'idle';
        })
        .catch(function () {
          state = 'idle';
          button.disabled = false;
          button.textContent = idleLabel;
          toast(strings.cartAddFailed || 'ما قدرت أضيفها للسلة، جرب مرة ثانية', 'error');
        });
    });
    row.appendChild(button);
    // The action row (copy/thumbs/regenerate) is appended at `done`, after
    // this — but if it is already there, the CTA stays above it.
    var actions = bubbleEl.querySelector('.eros-assistant-actions');
    if (actions) bubbleEl.insertBefore(row, actions);
    else bubbleEl.appendChild(row);
    scrollToEnd();
    return true;
  }

  /* FS-026: Flutter shows product card rail only — no text link chips under it. */

  function appendCards(products) {
    if (!products || !products.length) return;
    var rail = document.createElement('div');
    rail.className = 'eros-assistant-cards';
    products.forEach(function (card) {
      if (!card || !card.handle) return;
      rail.appendChild(buildCardElement(card));
    });
    messagesEl.appendChild(rail);
    scrollToEnd();
    // Owner UI review 2026-08-18: images/hydration grow the rail AFTER the
    // first scroll, leaving the fresh recommendation cards below the fold on
    // desktop. Re-scroll on the next frames and whenever a card image loads.
    window.setTimeout(scrollToEnd, 120);
    window.setTimeout(scrollToEnd, 450);
    rail.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', scrollToEnd, { once: true });
    });

    products.forEach(function (card) {
      if (!card || !card.handle) return;
      // Enriched cards rendered complete already — no N+1 fetch needed.
      var el = rail.querySelector('[data-handle="' + CSS.escape(card.handle) + '"]');
      if (el && el.dataset.enriched === '1') return;
      hydrateCard(rail, card.handle);
    });
  }

  // Storefront amounts arrive in MAJOR units ("23250.0" IQD) — unlike
  // /products/{handle}.js which is minor units — so this must NOT divide
  // by 100 the way formatPrice does.
  function formatAmount(amount, currency) {
    var value = Number(amount);
    if (!isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || currencyCode,
        maximumFractionDigits: 0,
      }).format(value);
    } catch (e) {
      return value.toFixed(0) + ' ' + (currency || currencyCode);
    }
  }

  function buildCardElement(card) {
    var link = document.createElement('a');
    link.className = 'eros-assistant-card';
    link.href = '/products/' + card.handle;
    link.dataset.handle = card.handle;

    // The worker enriches cards with title/price/image from the same tool
    // rows that grounded them — render instantly when present and skip the
    // per-card /products/{handle}.js fetch; the fetch stays as the fallback
    // for cards that arrive reference-only (e.g. find_shades rows).
    var enriched = !!(card.title || card.image_url);

    var img;
    if (card.image_url) {
      img = document.createElement('img');
      img.src = card.image_url;
      img.alt = card.title || '';
      img.loading = 'lazy';
      img.className = 'eros-assistant-card__image';
    } else {
      img = document.createElement('div');
      img.className = 'eros-assistant-card__image';
    }

    var title = document.createElement('p');
    title.className = 'eros-assistant-card__title';
    title.textContent = card.title || card.handle.replace(/-/g, ' ');

    var price = document.createElement('p');
    price.className = 'eros-assistant-card__price';
    if (card.price != null) {
      if (card.on_sale && card.compare_at_price != null) {
        var current = document.createElement('span');
        current.textContent = formatAmount(card.price, card.currency);
        var old = document.createElement('s');
        old.className = 'eros-assistant-card__price-compare';
        old.textContent = ' ' + formatAmount(card.compare_at_price, card.currency);
        price.appendChild(current);
        price.appendChild(old);
      } else {
        price.textContent = formatAmount(card.price, card.currency);
      }
    }

    link.appendChild(img);
    link.appendChild(title);
    link.appendChild(price);
    if (enriched) link.dataset.enriched = '1';
    return link;
  }

  function hydrateCard(rail, handle) {
    fetch('/products/' + handle + '.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (product) {
        if (!product) return;
        var el = rail.querySelector('[data-handle="' + CSS.escape(handle) + '"]');
        if (!el) return;
        var imgHolder = el.querySelector('.eros-assistant-card__image');
        if (product.featured_image) {
          var img = document.createElement('img');
          img.src = product.featured_image;
          img.alt = product.title || '';
          img.loading = 'lazy';
          img.className = 'eros-assistant-card__image';
          imgHolder.replaceWith(img);
        }
        var titleEl = el.querySelector('.eros-assistant-card__title');
        if (titleEl && product.title) titleEl.textContent = product.title;
        var priceEl = el.querySelector('.eros-assistant-card__price');
        if (priceEl && typeof product.price === 'number') priceEl.textContent = formatPrice(product.price);
      })
      .catch(function () {
        // Leave the placeholder + handle-derived text — non-fatal.
      });
  }

  // Gemini-style stop control: while streaming, the send button STAYS
  // enabled and becomes a stop-square that aborts the in-flight request —
  // the whole composer used to be disabled, leaving no way to interrupt a
  // long (15-25s on complex turns) generation.
  var streamAbort = null;
  var sendButtonHtml = sendButton ? sendButton.innerHTML : '';
  var STOP_ICON_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
    '<rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"></rect></svg>';

  function setStreaming(next) {
    streaming = next;
    if (input) input.disabled = next;
    if (menuButton) menuButton.disabled = next;
    if (newChatButton) newChatButton.disabled = next;
    if (micButton) micButton.disabled = next;
    if (attachButton) attachButton.disabled = next;
    if (sendButton) {
      sendButton.disabled = false;
      sendButton.classList.toggle('eros-assistant-panel__send--stop', next);
      if (next) {
        var strings = window.ErosAssistantStrings || {};
        sendButton.innerHTML = STOP_ICON_SVG;
        sendButton.setAttribute('aria-label', strings.stop || 'إيقاف');
      } else {
        sendButton.innerHTML = sendButtonHtml;
        sendButton.removeAttribute('aria-label');
        // Back to a send button: it only lights up with text or an image.
        updateSendEnabled();
      }
    }
  }

  function stopStreaming() {
    if (streamAbort) {
      try {
        streamAbort.abort();
      } catch (e) {
        /* already settled */
      }
    }
  }

  function renderSuggestionChips() {
    chipsEl.innerHTML = '';
    if (hasMessages) return;
    var strings = window.ErosAssistantStrings || {};
    var labels = [strings.suggestion1, strings.suggestion2, strings.suggestion3].filter(Boolean);
    labels.forEach(function (label) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'eros-assistant-chip';
      chip.textContent = label;
      chip.addEventListener('click', function () {
        if (streaming) return;
        submitMessage(label);
      });
      chipsEl.appendChild(chip);
    });
  }

  // Parses one SSE frame block ("event: x\ndata: {...}") into {event, data}.
  function parseFrame(raw) {
    var eventName = 'message';
    var dataLines = [];
    raw.split('\n').forEach(function (line) {
      if (line.indexOf('event:') === 0) {
        eventName = line.slice(6).trim();
      } else if (line.indexOf('data:') === 0) {
        dataLines.push(line.slice(5).trim());
      }
    });
    var payload = null;
    try {
      payload = JSON.parse(dataLines.join('\n'));
    } catch (e) {
      payload = null;
    }
    return { event: eventName, data: payload };
  }

  // Guest-gate CTA: rendered under the wall reply when the worker sends
  // login_required. Opens the same OTP auth sheet the stock-notify soft gate
  // uses; on successful sign-in the sheet closes and the claim flow adopts
  // the whole guest conversation into the account — nothing is lost.
  function renderLoginCta() {
    var strings = window.ErosAssistantStrings || {};
    var row = document.createElement('div');
    row.className = 'eros-assistant-login-cta';
    row.style.cssText = 'display:flex;justify-content:center;padding:8px 0 4px;';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button eros-assistant-login-cta__button';
    btn.textContent = strings.signIn || 'سجّل الدخول للمتابعة 🤍';
    btn.addEventListener('click', openAuthSheet);
    row.appendChild(btn);
    messagesEl.appendChild(row);
    scrollToEnd();
  }

  function openAuthSheet() {
    if (!(window.ErosUI && window.ErosUI.openSheet && window.ErosAccountAuth)) {
      window.location.href = '/account';
      return;
    }
    var strings = window.ErosAssistantStrings || {};
    var wrap = window.ErosUI.el('div');
    var panelEl = window.ErosUI.el('div', '', { 'data-eros-auth-panel': '' });
    wrap.appendChild(panelEl);
    var sheet = window.ErosUI.openSheet({
      title: strings.signInTitle || 'تسجيل الدخول',
      content: wrap,
      onClose: function () {},
    });
    window.ErosAccountAuth.mountAll();
    var unsubscribe = window.ErosSession.onChange(function (session) {
      if (session && session.customerGid) {
        unsubscribe();
        sheet.close();
      }
    });
  }

  async function streamChat(message, images) {
    // Mint the conversation id BEFORE the request, not in persistExchange
    // after it: otherwise the opening turn is sent with no id, the backend
    // stores it under an id of its own, and every later turn threads under a
    // different (locally generated) one — splitting one conversation in two.
    if (!currentConversationId) currentConversationId = uid();

    streamAbort = new AbortController();
    var response = await fetch(BASE_URL + CHAT_PATH, {
      method: 'POST',
      credentials: 'include',
      signal: streamAbort.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({
        message: message,
        history: history,
        language: languageCode(),
        customer_id: customerId() || undefined,
        // The commerce JWT is no longer held client-side — the worker reads
        // the HttpOnly `eros_session` cookie (credentials:'include' above)
        // to identify a signed-in customer instead.
        // Threads this turn into the same stored conversation, which the
        // backend replays as history — so it survives sign-out and follows
        // the account to other devices, unlike the localStorage copy.
        conversation_id: currentConversationId || undefined,
        anon_id: customerId() ? undefined : anonId() || undefined,
        // ADR-270c: cart count + page; omitted when unknown (the 6-message guest limit then holds).
        purchase_intent: A.purchaseIntent ? A.purchaseIntent() : undefined,
        // TRUE-streaming opt-in: this client understands the `replace`
        // event (guard rewrites), so the worker may send live deltas.
        stream: true,
        // Multimodal turn (contract 2026-08-24): each entry is already
        // downscaled to a 1024px max edge and re-encoded as JPEG q0.8
        // client-side; `data` is bare base64 with the data-URI prefix
        // stripped, and `message` may be '' when attachments ride along.
        // Exactly ONE attachment keeps the legacy single `image` field
        // (accepted forever); 2-3 travel as the `images` array. Never both,
        // and both omitted entirely when unattached.
        image:
          images && images.length === 1
            ? { mime_type: images[0].mime_type, data: images[0].data }
            : undefined,
        images:
          images && images.length > 1
            ? images.map(function (img) {
                return { mime_type: img.mime_type, data: img.data };
              })
            : undefined,
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error('assistant_request_failed');
    }

    var reader = response.body.getReader();
    var decoder = new TextDecoder();
    var buffer = '';
    var statusHandle = null;
    var assistantBubble = null;
    var assistantText = '';
    var cardsForMessage = null;
    // Only whether this turn produced a cart CTA — the tokenized checkout URL
    // itself is deliberately never carried into persistence.
    var hadCartAction = false;

    function clearStatus() {
      if (statusHandle) {
        statusHandle.el.remove();
        statusHandle = null;
      }
    }

    function ensureAssistantBubble() {
      if (assistantBubble) return assistantBubble;
      assistantBubble = appendBubble('assistant');
      var cursor = document.createElement('span');
      cursor.className = 'eros-assistant-cursor';
      cursor.textContent = '▌';
      assistantBubble.text.appendChild(cursor);
      assistantBubble.cursor = cursor;
      return assistantBubble;
    }

    // A user-initiated stop is a normal outcome, not an error: keep whatever
    // text already streamed, thread it into history, and persist the partial
    // exchange — matching how Gemini's stop button behaves.
    function finalizePartialOnStop() {
      clearStatus();
      if (assistantBubble && assistantBubble.cursor) assistantBubble.cursor.remove();
      if (assistantText) {
        history.push({ role: 'user', text: message });
        history.push({ role: 'model', text: assistantText });
        persistExchange(message, assistantText, cardsForMessage, images, hadCartAction);
        if (assistantBubble) {
          attachAssistantActions(assistantBubble.bubble, {
            seq: null,
            fb: null,
            // The assistant entry persistExchange just appended — pinning the
            // index keeps a later vote on this bubble from landing on a
            // newer entry.
            msgIndex: loadMessages(currentConversationId).length - 1,
          });
          refreshRegenerateAction();
        }
      }
    }

    while (true) {
      var chunk;
      try {
        chunk = await reader.read();
      } catch (e) {
        if (e && (e.name === 'AbortError' || e.code === 20)) {
          finalizePartialOnStop();
          return;
        }
        throw e;
      }
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });

      var frames = buffer.split('\n\n');
      buffer = frames.pop();

      for (var i = 0; i < frames.length; i++) {
        if (!frames[i].trim()) continue;
        var frame = parseFrame(frames[i]);
        if (!frame.data) continue;

        if (frame.event === 'status') {
          // One status row per response, updated in place — a new tool step
          // replaces the previous label instead of stacking rows.
          if (!statusHandle) statusHandle = appendStatus(frame.data.text || '');
          else statusHandle.textEl.textContent = frame.data.text || '';
        } else if (frame.event === 'delta') {
          clearStatus();
          ensureAssistantBubble();
          assistantText += frame.data.text || '';
          // Strip markdown bold/italic markers — rendered as plain text
          // (textContent, never innerHTML) so **bold** would otherwise
          // show as literal asterisks.
          renderAssistantText(assistantBubble.text, assistantText);
          assistantBubble.text.appendChild(assistantBubble.cursor);
          scrollToEnd();
        } else if (frame.event === 'replace') {
          // True-streaming worker: swap the in-progress bubble's ENTIRE text
          // with data.text ('' = clear) — used when the server revises what
          // it already streamed.
          clearStatus();
          ensureAssistantBubble();
          assistantText = typeof frame.data.text === 'string' ? frame.data.text : '';
          renderAssistantText(assistantBubble.text, assistantText);
          assistantBubble.text.appendChild(assistantBubble.cursor);
          scrollToEnd();
        } else if (frame.event === 'cart') {
          // `data` IS the action object (no wrapper key). It lands just
          // before `cards`, so the CTA sits inside the reply bubble and the
          // product rail renders underneath it.
          clearStatus();
          ensureAssistantBubble();
          if (attachCartAction(assistantBubble.bubble, frame.data)) hadCartAction = true;
        } else if (frame.event === 'cards') {
          cardsForMessage = frame.data.products || null;
          appendCards(cardsForMessage);
        } else if (frame.event === 'login_required') {
          renderLoginCta();
        } else if (frame.event === 'done') {
          if (assistantBubble && assistantBubble.cursor) assistantBubble.cursor.remove();
          if (frame.data && frame.data.anon_id) saveAnonId(frame.data.anon_id);
          history.push({ role: 'user', text: message });
          history.push({ role: 'model', text: assistantText });
          persistExchange(message, assistantText, cardsForMessage, images, hadCartAction);
          if (assistantBubble && assistantText) {
            attachAssistantActions(assistantBubble.bubble, {
              seq: null,
              fb: null,
              msgIndex: loadMessages(currentConversationId).length - 1,
            });
            refreshRegenerateAction();
          }
        } else if (frame.event === 'error') {
          clearStatus();
          throw new Error(frame.data.message || 'assistant_error');
        }
      }
    }
  }

  function persistExchange(userText, assistantText, cards, images, hadCart) {
    if (!currentConversationId) currentConversationId = uid();
    var stored = loadMessages(currentConversationId);
    // Base64 NEVER enters localStorage (5MB quota) — the data URIs go to the
    // IndexedDB image store and the entry keeps only a small `imgKey`
    // pointer (owner order 2026-08-25; restored turns used to collapse to a
    // 📎 marker). `hadImage` stays alongside it as the fallback marker for
    // browsers where the IndexedDB write failed.
    var hadImage = !!(images && images.length);
    var userEntry = { role: 'user', text: userText };
    if (hadImage) {
      userEntry.hadImage = true;
      userEntry.imgKey = uid();
      saveTurnImages(
        userEntry.imgKey,
        currentConversationId,
        images.map(function (img) { return img.dataUrl; }),
      );
    }
    stored.push(userEntry);
    var assistantEntry = { role: 'assistant', text: assistantText, cards: cards || null };
    // Same rule for the cart CTA: the checkout URL is a short-lived tokenized
    // cart permalink, so storing it would repaint a DEAD button after a
    // reload. Only the fact that this turn had one is kept — see
    // renderMessagesFromStorage for why the restore path renders nothing.
    if (hadCart) assistantEntry.hadCart = true;
    stored.push(assistantEntry);
    saveMessages(currentConversationId, stored);

    var meta = listConversations().find(function (c) { return c.id === currentConversationId; });
    var strings = window.ErosAssistantStrings || {};
    // An image-only opening turn has no text to title the thread with.
    var title = meta
      ? meta.title
      : autoTitle(userText) || (hadImage ? strings.imageAttached || 'صورة مرفقة' : '');
    saveConversationMeta(currentConversationId, title);
  }

  async function submitMessage(message, imagesOverride) {
    if (streaming) return;
    message = (message || '').trim();
    // Image-only turns are valid (the worker accepts message: '' when
    // images ride along). imagesOverride is the retry path re-sending the
    // exact attachments that failed.
    var images = imagesOverride || (pendingImages.length ? pendingImages : null);
    if (!message && !images) return;
    // A pending recognition result must not repopulate the field after send.
    stopVoice(true);

    hasMessages = true;
    updateNewChatVisibility();
    renderSuggestionChips();
    removeErrorRows();
    lastFailedMessage = null;
    lastFailedImages = null;
    // Sending re-pins: the user is at the composer, so the new exchange
    // should always come into view.
    pinnedToBottom = true;
    updateJumpButton();
    var userBubble = appendBubble('user');
    userBubble.text.textContent = message;
    if (images) {
      images.forEach(function (img) {
        attachUserThumb(userBubble.bubble, img.dataUrl);
      });
    }
    if (input) input.value = '';
    // Only the composer's own attachments are consumed — a retry must not
    // wipe images the user picked while the error row was up. (Identity
    // check works because clearPendingImages reassigns, never truncates.)
    if (images === pendingImages) clearPendingImages();
    setStreaming(true);

    try {
      await streamChat(message, images);
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.code === 20)) {
        // User pressed stop before any bytes arrived — nothing to show, and
        // definitely not an error row.
      } else {
        lastFailedMessage = message;
        lastFailedImages = images;
        var strings = window.ErosAssistantStrings || {};
        appendError(strings.error || 'Something went wrong.', strings.retry || 'Retry');
      }
    } finally {
      streamAbort = null;
      setStreaming(false);
      renderSuggestionChips();
      updateNewChatVisibility();
      if (input) input.focus();
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    stopVoice(true);
    if (streaming) {
      stopStreaming();
      return;
    }
    if (!input) return;
    submitMessage(input.value);
  }

  function renderMessagesFromStorage(msgs) {
    messagesEl.innerHTML = '';
    history = [];
    hasMessages = !!(msgs && msgs.length);
    pinnedToBottom = true;
    updateJumpButton();
    if (!msgs || !msgs.length) {
      renderEmptyGreeting();
      renderSuggestionChips();
      updateNewChatVisibility();
      return;
    }
    renderGuestHint();
    msgs.forEach(function (msg, index) {
      if (msg.role === 'user') {
        var userBubble = appendBubble('user');
        userBubble.text.textContent = msg.text || '';
        if (msg.imgDeleted) attachDeletedImageMark(userBubble.bubble);
        else if (msg.imgKey) restoreUserImages(userBubble.bubble, msg.imgKey);
        else if (msg.hadImage) attachUserImageMarker(userBubble.bubble);
        history.push({ role: 'user', text: msg.text });
      } else {
        var bubble = appendBubble('assistant');
        renderAssistantText(bubble.text, msg.text || '');
        attachAssistantActions(bubble.bubble, {
          seq: msg.seq != null ? msg.seq : null,
          fb: msg.fb || null,
          msgIndex: index,
        });
        // msg.hadCart is deliberately NOT rendered as anything. The checkout
        // permalink it belonged to was never stored (and would have expired),
        // and the assistant's basket is a worker-built Storefront API cart —
        // NOT this browser's /cart — so a substitute link would point at the
        // wrong basket. A restored turn therefore shows no cart button at all.
        if (msg.cards && msg.cards.length) appendCards(msg.cards);
        history.push({ role: 'model', text: msg.text });
      }
    });
    refreshRegenerateAction();
    renderSuggestionChips();
    updateNewChatVisibility();
    scrollToEnd(true);
  }

  function startNewChat() {
    currentConversationId = null;
    history = [];
    hasMessages = false;
    lastFailedMessage = null;
    lastFailedImages = null;
    clearPendingImages();
    pinnedToBottom = true;
    updateJumpButton();
    removeErrorRows();
    renderEmptyGreeting();
    renderSuggestionChips();
    updateNewChatVisibility();
    if (input) input.focus();
  }

  function openConversation(id) {
    currentConversationId = id;
    var local = loadMessages(id);
    if (local && local.length) {
      renderMessagesFromStorage(local);
      return;
    }
    // Not on this device — hydrate from the account's server history
    // (a thread started on the Flutter app, for example), then cache it
    // locally so the next open is instant.
    if (!(window.ErosSession && window.ErosSession.isSignedIn())) {
      renderMessagesFromStorage(local);
      return;
    }
    A.fetchConversationMessages(id)
      .then(function (msgs) {
        if (!msgs) return;
        saveMessages(id, msgs);
        if (currentConversationId === id) renderMessagesFromStorage(msgs);
        downloadServerImages(id, msgs);
      })
      .catch(function () {
        /* offline — stays on the empty view */
      });
  }

  // ---- Voice input (Web Speech API), audit vs Gemini web 2026-08-18.
  // Feature-detected: the mic button is only created when the API exists
  // (Chromium; WebKit builds expose webkitSpeechRecognition) — Firefox and
  // older browsers simply never get the button.
  var SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
  var VOICE_LANGS = ['ar-IQ', 'ar-SA'];
  var voiceLangIndex = 0;
  var voiceRetryPending = false;
  var recognition = null;
  var listening = false;
  var micButton = null;
  var micHint = null;
  var micHintTimer = 0;
  var micPointerDownAt = 0;
  var speechBaseText = '';
  var inputPlaceholder = input ? input.placeholder : '';
  // Discard token: a graceful (blur) stop keeps the rec object alive to
  // deliver its final result — if a SEND happens in that window, the late
  // result must not repopulate the just-cleared field. Every started session
  // gets an id; discarding invalidates all ids issued so far.
  var voiceSessionId = 0;
  var voiceDiscardBelow = 0;

  function showMicHint(text) {
    if (!micHint || !text) return;
    micHint.textContent = text;
    micHint.hidden = false;
    if (micHintTimer) window.clearTimeout(micHintTimer);
    micHintTimer = window.setTimeout(function () {
      micHint.hidden = true;
    }, 4000);
  }

  function setListeningUI(on) {
    if (micButton) {
      micButton.classList.toggle('eros-assistant-panel__mic--listening', on);
      micButton.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    if (input) {
      var strings = window.ErosAssistantStrings || {};
      input.placeholder = on ? strings.voiceListening || 'جاري الاستماع…' : inputPlaceholder;
    }
  }

  // discard=true (send / panel close / identity switch): abort and silence
  // any pending final result. discard=false (mic toggle / input blur): stop
  // gracefully so the final transcript still lands in the field for review.
  function stopVoice(discard) {
    listening = false;
    setListeningUI(false);
    if (discard) voiceDiscardBelow = voiceSessionId;
    if (!recognition) return;
    var rec = recognition;
    recognition = null;
    if (discard) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try {
        rec.abort();
      } catch (e) {
        /* already settled */
      }
    } else {
      try {
        rec.stop();
      } catch (e) {
        /* already settled */
      }
    }
  }

  function startVoice() {
    if (!SpeechRec || listening || streaming) return;
    var strings = window.ErosAssistantStrings || {};
    var rec;
    try {
      rec = new SpeechRec();
    } catch (e) {
      showMicHint(strings.voiceUnavailable || 'الصوت غير متاح');
      return;
    }
    recognition = rec;
    listening = true;
    var session = ++voiceSessionId;
    speechBaseText = input ? input.value.trim() : '';
    rec.lang = VOICE_LANGS[voiceLangIndex];
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    rec.onresult = function (event) {
      if (!input || session <= voiceDiscardBelow) return;
      // Interim + final segments concatenated — the transcript streams into
      // the field and STAYS there for review/edit; sending is manual.
      var transcript = '';
      for (var i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      transcript = transcript.trim();
      if (!transcript) return;
      input.value = speechBaseText ? speechBaseText + ' ' + transcript : transcript;
      // Programmatic value changes fire no 'input' event — the send button
      // would stay dimmed on a dictated-only message without this.
      updateSendEnabled();
    };
    rec.onerror = function (event) {
      var code = event && event.error;
      if (code === 'language-not-supported' && voiceLangIndex < VOICE_LANGS.length - 1) {
        // ar-IQ not on this engine — retry once with ar-SA (after onend).
        voiceLangIndex += 1;
        voiceRetryPending = true;
        return;
      }
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        showMicHint(strings.voicePermission || 'اضغط للسماح بالمايك');
      } else if (code === 'no-speech') {
        showMicHint(strings.voiceNoSpeech || 'ما وصلنا صوت — جرب مرة ثانية');
      } else if (code !== 'aborted') {
        showMicHint(strings.voiceUnavailable || 'الصوت غير متاح');
      }
    };
    rec.onend = function () {
      if (recognition === rec) recognition = null;
      listening = false;
      setListeningUI(false);
      if (voiceRetryPending) {
        voiceRetryPending = false;
        startVoice();
      }
    };
    try {
      rec.start();
      setListeningUI(true);
    } catch (e) {
      recognition = null;
      listening = false;
      setListeningUI(false);
      showMicHint(strings.voiceUnavailable || 'الصوت غير متاح');
    }
  }

  if (SpeechRec && form && input && sendButton && sendButton.parentNode) {
    var micStrings = window.ErosAssistantStrings || {};
    micButton = document.createElement('button');
    micButton.type = 'button';
    micButton.className = 'eros-assistant-panel__mic';
    micButton.setAttribute('aria-label', micStrings.voiceInput || 'إدخال صوتي');
    micButton.title = micStrings.voiceInput || 'إدخال صوتي';
    micButton.setAttribute('aria-pressed', 'false');
    micButton.innerHTML = MIC_ICON_SVG;
    micButton.addEventListener('pointerdown', function () {
      micPointerDownAt = Date.now();
    });
    micButton.addEventListener('click', function () {
      if (streaming) return;
      if (listening) stopVoice(false);
      else startVoice();
    });
    sendButton.parentNode.insertBefore(micButton, sendButton);

    micHint = document.createElement('p');
    micHint.className = 'eros-assistant-mic-hint';
    micHint.hidden = true;
    form.appendChild(micHint);

    // Blur ends dictation (final text stays for review) — unless the blur
    // came from pressing the mic button itself (that press IS the toggle).
    input.addEventListener('blur', function () {
      if (!listening) return;
      if (Date.now() - micPointerDownAt < 600) return;
      stopVoice(false);
    });
  }

  // ---- Image attachment (2026-08-19, multi-image 2026-08-24). Built exactly
  // like the mic button: JS-created chrome inserted into the composer bar,
  // strings via window.ErosAssistantStrings with Arabic fallbacks. Picked
  // files are never uploaded as-is — each is decoded, downscaled to a 1024px
  // max edge and re-encoded as JPEG q0.8 in-page, and only the bare base64
  // travels (worker contract: {mime_type,data}, base64 length ≤ 2,000,000
  // per image, at most 3 images per turn).
  var IMAGE_MAX_EDGE_PX = 1024;
  var IMAGE_JPEG_QUALITY = 0.8;
  var IMAGE_MAX_BASE64_LEN = 2000000;
  var IMAGE_MAX_COUNT = 3;
  var IMAGE_ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
  // Array of { mime_type: 'image/jpeg', data: '<base64>', dataUrl: 'data:…' }.
  // clearPendingImages REASSIGNS (never truncates) so a send/retry that
  // captured the array keeps its contents for the request and error paths.
  var pendingImages = [];
  var lastFailedImages = null;
  var attachButton = null;
  var attachInput = null;
  var attachmentsEl = null;

  // The mic owns the hint element when speech is supported; on engines
  // without it (Firefox) there is no hint yet — create the same node so both
  // features share one slot and showMicHint's auto-dismiss timer.
  function ensureComposerHint() {
    if (micHint || !form) return micHint;
    micHint = document.createElement('p');
    micHint.className = 'eros-assistant-mic-hint';
    micHint.hidden = true;
    form.appendChild(micHint);
    return micHint;
  }

  function showComposerHint(text) {
    ensureComposerHint();
    showMicHint(text);
  }

  function updateSendEnabled() {
    // While streaming the button is the stop control — always pressable.
    if (!sendButton || streaming) return;
    if (!input) {
      sendButton.disabled = false;
      return;
    }
    sendButton.disabled = !input.value.trim() && !pendingImages.length;
  }

  // createImageBitmap decodes off the main thread and honours EXIF
  // orientation; the <img> fallback covers engines that reject the options
  // bag or the file type.
  function decodeImageElement(file) {
    return new Promise(function (resolve, reject) {
      var objectUrl = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('decode_failed'));
      };
      img.src = objectUrl;
    });
  }

  function decodeImage(file) {
    if (window.createImageBitmap) {
      try {
        return window
          .createImageBitmap(file, { imageOrientation: 'from-image' })
          .catch(function () {
            return decodeImageElement(file);
          });
      } catch (e) {
        return decodeImageElement(file);
      }
    }
    return decodeImageElement(file);
  }

  function downscaleToBase64(file) {
    return decodeImage(file).then(function (source) {
      var width = source.naturalWidth || source.width;
      var height = source.naturalHeight || source.height;
      if (!width || !height) throw new Error('decode_failed');
      if (width > IMAGE_MAX_EDGE_PX || height > IMAGE_MAX_EDGE_PX) {
        if (width >= height) {
          height = Math.max(1, Math.round(height * (IMAGE_MAX_EDGE_PX / width)));
          width = IMAGE_MAX_EDGE_PX;
        } else {
          width = Math.max(1, Math.round(width * (IMAGE_MAX_EDGE_PX / height)));
          height = IMAGE_MAX_EDGE_PX;
        }
      }
      var canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      var ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('encode_failed');
      ctx.drawImage(source, 0, 0, width, height);
      // Release the bitmap's backing memory as soon as it is painted.
      if (typeof source.close === 'function') source.close();
      var dataUrl = canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY);
      var comma = dataUrl.indexOf(',');
      var data = comma >= 0 ? dataUrl.slice(comma + 1) : '';
      if (!data) throw new Error('encode_failed');
      // An engine without a JPEG encoder silently hands back PNG — declare
      // what was actually produced rather than what was asked for.
      var declared = (dataUrl.slice(0, comma).match(/^data:([^;,]+)/) || [])[1];
      var mime = IMAGE_ALLOWED_MIMES.indexOf(declared) >= 0 ? declared : 'image/jpeg';
      return { dataUrl: dataUrl, data: data, mime_type: mime };
    });
  }

  function clearPendingImages() {
    // Reassign, never truncate — see the pendingImages declaration.
    pendingImages = [];
    renderAttachments();
    updateSendEnabled();
  }

  function removePendingImage(index) {
    // Same reassign rule: an in-flight send captured the old array.
    pendingImages = pendingImages.filter(function (_, i) { return i !== index; });
    renderAttachments();
    updateSendEnabled();
  }

  function renderAttachments() {
    if (!attachmentsEl) return;
    attachmentsEl.innerHTML = '';
    if (!pendingImages.length) {
      attachmentsEl.hidden = true;
      return;
    }
    var strings = window.ErosAssistantStrings || {};
    attachmentsEl.hidden = false;
    pendingImages.forEach(function (pending, index) {
      var chip = document.createElement('div');
      chip.className = 'eros-assistant-attachment';
      var img = document.createElement('img');
      img.className = 'eros-assistant-attachment__image';
      img.src = pending.dataUrl;
      img.alt = strings.imageAttached || 'صورة مرفقة';
      var removeLabel = strings.imageRemove || 'إزالة الصورة';
      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'eros-assistant-attachment__remove';
      remove.setAttribute('aria-label', removeLabel);
      remove.title = removeLabel;
      remove.textContent = '×';
      remove.addEventListener('click', function () {
        removePendingImage(index);
        if (input) input.focus();
      });
      chip.appendChild(img);
      chip.appendChild(remove);
      attachmentsEl.appendChild(chip);
    });
  }

  function handleImagePick(file) {
    if (!file) return Promise.resolve();
    var strings = window.ErosAssistantStrings || {};
    return downscaleToBase64(file)
      .then(function (result) {
        // 1024/q0.8 lands far under the cap for ordinary photos; a synthetic
        // or extremely noisy image can still overshoot — skip just this file
        // (the others stay attached) rather than let the worker reject the
        // whole turn.
        if (result.data.length > IMAGE_MAX_BASE64_LEN) {
          showComposerHint(strings.imageTooLarge || 'الصورة كبيرة، جرب صورة أصغر');
          return;
        }
        // Cap re-checked at completion time: a second picker round can race
        // an in-flight encode, and this guard is the invariant that the
        // pending set never exceeds the contract's 3.
        if (pendingImages.length >= IMAGE_MAX_COUNT) {
          showComposerHint(strings.imageLimit || '3 صور كحد أقصى بالرسالة الوحدة');
          return;
        }
        pendingImages = pendingImages.concat([{
          mime_type: result.mime_type || 'image/jpeg',
          data: result.data,
          dataUrl: result.dataUrl,
        }]);
        renderAttachments();
        updateSendEnabled();
        if (input) input.focus();
      })
      .catch(function () {
        // Undecodable (e.g. HEIC where the engine has no decoder), no 2D
        // context, or out of memory — the generic composer error covers it.
        showComposerHint(strings.error || 'صار خلل، جرب مرة ثانية.');
      });
  }

  function handleImagePicks(files) {
    if (!files || !files.length) return;
    var strings = window.ErosAssistantStrings || {};
    var room = IMAGE_MAX_COUNT - pendingImages.length;
    if (files.length > room) {
      // Over the 3-per-message cap: keep what fits, toast about the rest —
      // matching the worker's polite coded rejection instead of a hard fail.
      showComposerHint(strings.imageLimit || '3 صور كحد أقصى بالرسالة الوحدة');
      if (room <= 0) return;
      files = files.slice(0, room);
    }
    // Sequential, not parallel: keeps thumbnails in pick order and bounds
    // decode memory to one bitmap at a time on low-end phones.
    files.reduce(function (chain, file) {
      return chain.then(function () {
        return handleImagePick(file);
      });
    }, Promise.resolve());
  }

  if (form && input && sendButton && sendButton.parentNode) {
    var attachStrings = window.ErosAssistantStrings || {};
    var attachLabel = attachStrings.attachImage || 'إرفاق صورة';

    attachInput = document.createElement('input');
    attachInput.type = 'file';
    attachInput.accept = 'image/*';
    attachInput.multiple = true;
    attachInput.hidden = true;
    attachInput.className = 'eros-assistant-panel__attach-input';
    attachInput.addEventListener('change', function () {
      // Copy out before resetting — clearing `value` empties the FileList.
      var files = attachInput.files ? Array.prototype.slice.call(attachInput.files) : [];
      // Reset first — re-picking the SAME file must fire `change` again.
      attachInput.value = '';
      handleImagePicks(files);
    });

    attachButton = document.createElement('button');
    attachButton.type = 'button';
    attachButton.className = 'eros-assistant-panel__attach';
    attachButton.setAttribute('aria-label', attachLabel);
    attachButton.title = attachLabel;
    attachButton.innerHTML = ATTACH_ICON_SVG;
    attachButton.addEventListener('click', function () {
      if (streaming) return;
      attachInput.click();
    });

    // Order in the bar: [input] 📎 🎤 [send] — the mic may be absent.
    sendButton.parentNode.insertBefore(attachButton, micButton || sendButton);
    form.appendChild(attachInput);

    attachmentsEl = document.createElement('div');
    attachmentsEl.className = 'eros-assistant-attachments';
    attachmentsEl.hidden = true;
    // Chip row sits above the input row; old cached markup without the bar
    // wrapper falls back to appending at the end of the composer.
    var composerBar = form.querySelector('.eros-assistant-panel__composer-bar');
    if (composerBar) form.insertBefore(attachmentsEl, composerBar);
    else form.appendChild(attachmentsEl);

    input.addEventListener('input', updateSendEnabled);
  }
  updateSendEnabled();

  // ADR-195 smart resume: set once the customer EXPLICITLY asks for a new
  // chat (header button or the panel's «محادثة جديدة»). For the rest of this
  // page's life, re-opening the drawer must not drag them back into the
  // last thread.
  var userStartedNewChat = false;
  function startNewChatByUser() {
    userStartedNewChat = true;
    startNewChat();
  }

  if (form) form.addEventListener('submit', handleSubmit);
  if (newChatButton) newChatButton.addEventListener('click', startNewChatByUser);
  if (deletePhotosButton) deletePhotosButton.addEventListener('click', openDeletePhotosConfirm);

  // ADR-195 conversation side panel (assets/eros-assistant-panel.js).
  var sidebar = V.panel.mount({
    root: sidebarEl,
    host: panel,
    toggleButton: menuButton,
    getCurrentId: function () {
      return currentConversationId;
    },
    onOpen: function (id) {
      openConversation(id);
    },
    onNewChat: startNewChatByUser,
    onDeleted: function (id) {
      if (id === currentConversationId) startNewChat();
    },
    onOpenChange: function (open) {
      // The identity overlay would otherwise sit over the chat behind the
      // panel's scrim.
      if (open) setIdentityOpen(false);
    },
  });

  // Venus rebrand (2026-08-24): the header title button toggles the static
  // identity overlay (Venus/Eros story cards). Presentation only — sending a
  // message closes it so the reply is visible; chat logic is untouched.
  function setIdentityOpen(open) {
    if (!identityOverlay) return;
    identityOverlay.hidden = !open;
    if (identityButton) identityButton.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  if (identityButton && identityOverlay) {
    identityButton.addEventListener('click', function () {
      setIdentityOpen(identityOverlay.hidden);
    });
  }
  if (identityCloseButton) {
    identityCloseButton.addEventListener('click', function () {
      setIdentityOpen(false);
    });
  }
  if (form && identityOverlay) {
    form.addEventListener('submit', function () {
      setIdentityOpen(false);
    });
  }

  renderEmptyGreeting();
  renderSuggestionChips();

  // ---- ADR-195 entry rule (smart resume), same as the app's
  // PickEntryConversationUseCase: opening Venus WITHOUT a target resumes the
  // last conversation if it was active in the last 24 h; older → the fresh
  // greeting. Only a pristine chat is ever replaced — nothing open, nothing
  // typed or attached, not streaming, and no explicit «محادثة جديدة» this
  // page. A targeted open (window.ErosAssistant.openConversation, the store
  // drawer's «آخر المحادثات») sets currentConversationId before the drawer
  // opens, so it always wins.
  var entryFetchInFlight = false;
  function isPristine() {
    return (
      !userStartedNewChat &&
      !currentConversationId &&
      !hasMessages &&
      !streaming &&
      !(input && input.value.trim()) &&
      !pendingImages.length
    );
  }
  function applyEntryRule() {
    if (!isPristine()) return;
    var localId = V.conversations.pickEntry(listConversations(), Date.now());
    if (localId) {
      openConversation(localId);
      return;
    }
    // Nothing recent on this device — a signed-in account may have a thread
    // from the app or another browser. Re-check pristine when it lands: the
    // customer may have started typing meanwhile.
    if (entryFetchInFlight || !A.isSignedIn()) return;
    entryFetchInFlight = true;
    A.fetchServerConversations()
      .then(function (remote) {
        entryFetchInFlight = false;
        var remoteId = V.conversations.pickEntry(remote, Date.now());
        if (remoteId && isPristine()) openConversation(remoteId);
      })
      .catch(function () {
        entryFetchInFlight = false;
      });
  }

  // Exposed for drawer recent-chats (assets/eros-drawer-recent-chats.js).
  window.ErosAssistant = {
    listConversations: listConversations,
    renameConversation: renameConversation,
    openConversation: function (id) {
      openConversation(id);
      if (typeof panel.show === 'function') panel.show();
    },
    openPanel: function () {
      if (typeof panel.show === 'function') panel.show();
    },
  };

  // Focus the composer whenever the panel opens (the base DialogElement
  // toggles the `open` attribute — see assets/theme.js's DialogElement).
  // Closing the panel also ends any live dictation.
  // ADR-195: every open runs the entry rule; closing also folds the side
  // panel away so the next open starts on the chat.
  new MutationObserver(function () {
    if (panel.hasAttribute('open')) {
      applyEntryRule();
      if (input) {
        window.setTimeout(function () {
          input.focus();
        }, 150);
      }
    } else {
      stopVoice(true);
      if (sidebar) sidebar.close(false);
    }
  }).observe(panel, { attributes: true, attributeFilter: ['open'] });

  // This file is lazy-loaded by the very tap that opens the drawer
  // (eros-lazy.js primes on pointerdown), so the first open usually happens
  // BEFORE the observer above exists — apply the rule for that open here.
  if (panel.hasAttribute('open')) applyEntryRule();
})();
